// Adapted from herdr voice.ts, Copyright (c) 2026 devswha (MIT).
import { useEffect, useState } from 'react';
import { VoiceStatus } from '@pen/shared';
import type { z } from 'zod';
import { request } from '../api';

export const VOICE_CONFIG_EVENT = 'pen:voice-config';
export type VoiceStatusValue = z.infer<typeof VoiceStatus>;
let pending: Promise<VoiceStatusValue> | null = null;
let cached: VoiceStatusValue | null = null;
const listeners = new Set<(status: VoiceStatusValue | null) => void>();

export function refreshVoiceStatus(): Promise<VoiceStatusValue> {
  const next = request('GET', '/api/voice', undefined, VoiceStatus);
  pending = next;
  void next.then((status) => {
    if (pending !== next) return;
    cached = status;
    for (const listener of listeners) listener(status);
  }, () => {
    if (pending !== next) return;
    pending = null;
    cached = null;
    for (const listener of listeners) listener(null);
  });
  return next;
}
function onConfig(): void { void refreshVoiceStatus().catch(() => undefined); }

export function useVoiceStatus(): VoiceStatusValue | null {
  const [status, setStatus] = useState(cached);
  useEffect(() => {
    if (listeners.size === 0) window.addEventListener(VOICE_CONFIG_EVENT, onConfig);
    listeners.add(setStatus);
    let live = true;
    void (pending ?? refreshVoiceStatus()).then((value) => { if (live) setStatus(value); }, () => { if (live) setStatus(null); });
    return () => {
      live = false;
      listeners.delete(setStatus);
      if (!listeners.size) window.removeEventListener(VOICE_CONFIG_EVENT, onConfig);
    };
  }, []);
  return status;
}
